import type {
  DockmasterSpan,
  SpanEvent,
} from "../../../../../../../schemas/codegen";
import type {
  McpResponseData,
  McpToolInfo,
  ParsedJsonRpcResponse,
} from "./types";

export function extractMcpToolInfo(span: DockmasterSpan): McpToolInfo {
  const attributes = span.attributes || {};
  const toolName = attributes["mcp.tool.name"] as string | null;
  const methodName = attributes["mcp.method.name"] as string | null;
  const requestId = attributes["mcp.request.id"] as string | null;

  // Extract all request arguments
  const args: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(attributes)) {
    if (key.startsWith("mcp.request.argument.")) {
      const argName = key.replace("mcp.request.argument.", "");
      args[argName] = value;
    }
  }

  return {
    toolName,
    methodName,
    requestId,
    arguments: args,
  };
}

export function extractMcpResponseData(span: DockmasterSpan): McpResponseData {
  const attributes = span.attributes || {};
  const isStreaming = attributes["mcp.streaming.enabled"] === true;
  const messageCount =
    (attributes["mcp.streaming.message_count"] as number) || 0;
  const errorCount = (attributes["mcp.streaming.error_count"] as number) || 0;
  const totalBytes = attributes["mcp.streaming.total_bytes"] as number;
  const throughput = attributes["mcp.streaming.throughput_bps"] as number;
  const duration = span.duration || 0;

  if (!isStreaming) {
    // For non-streaming responses, look for response body in attributes
    const responseBody = attributes["mcp.response.body"] as string;
    return {
      isStreaming: false,
      responseData: responseBody || null,
      messageCount,
      errorCount,
      totalBytes,
      throughput,
      duration,
    };
  }

  // For streaming responses, concatenate response from chunk events
  let concatenatedResponse = "";

  if (span.events) {
    // Process all streaming chunk events and concatenate response
    const chunkEvents = span.events
      .filter((event: SpanEvent) => event.name === "mcp.streaming.chunk")
      .sort((a, b) => a.time - b.time);

    for (const event of chunkEvents) {
      const chunkContent = event.attributes.chunk_content as string;
      if (chunkContent) {
        concatenatedResponse += chunkContent;
      }
    }
  }

  return {
    isStreaming: true,
    responseData: concatenatedResponse || null,
    messageCount,
    errorCount,
    totalBytes,
    throughput,
    duration,
  };
}

export function parseServerSentEvent(
  data: string,
): ParsedJsonRpcResponse | null {
  try {
    // Parse Server-Sent Event format: "event: message\ndata: {JSON}"
    const lines = data.split("\n");
    let jsonData = "";

    for (const line of lines) {
      if (line.startsWith("data: ")) {
        jsonData = line.substring(6); // Remove "data: " prefix
        break;
      }
    }

    if (!jsonData) {
      return null;
    }

    return JSON.parse(jsonData) as ParsedJsonRpcResponse;
  } catch {
    try {
      // Fallback: try parsing as direct JSON
      return JSON.parse(data) as ParsedJsonRpcResponse;
    } catch {
      return null;
    }
  }
}

export { formatFileSize as formatBytes } from "@/lib/utils";

export function formatThroughput(bps: number): string {
  if (bps < 1000) {
    return `${bps} B/s`;
  }
  if (bps < 1000000) {
    return `${(bps / 1000).toFixed(1)} KB/s`;
  }
  return `${(bps / 1000000).toFixed(1)} MB/s`;
}
